/**
 * The hover language's plate: a rounded fill drawn BEHIND a row's content,
 * independent of the row's own box. A row whose content touches its edges
 * gets a plate that reaches past them (positive `bleed`); a row that spans its
 * card or menu edge to edge gets one pulled inside (negative `bleed`). The
 * host's layout, hit area and dividers never change: the plate is absolutely
 * positioned, ignores the pointer, and sits above the host's own background
 * but below its content (zIndex -1 inside the host's stacking context; every
 * react-native-web View is position: relative; z-index: 0).
 *
 * Web only, like the rest of the hover language: off web it renders nothing.
 * Platform.OS is read at render time so tests can flip it before mounting.
 * Selected rows reuse it with their own `color`, so selection and hover share
 * one shape.
 */
import React from 'react';
import { Platform, View } from 'react-native';
import { useThemeColors } from '@/hooks/shared/useThemeColors';
import { useRadius } from '@/hooks/shared/useRadius';

export type PlateBleed = number | { x?: number; y?: number };

export interface HighlightPlateProps {
  /** Painted when true; transparent otherwise (the fill fades in and out). */
  visible: boolean;
  /**
   * How far the plate reaches past the host's box: positive outward (content
   * flush with the host's edges), negative inward (a host that spans its card
   * or menu). A number is left and right only; `{ x, y }` adds top and bottom.
   */
  bleed?: PlateBleed;
  /** Fill while visible. Default: the hover tint, semantic.background.hover. */
  color?: string;
  /** Corner radius. Default: radius.sm. */
  radius?: number;
  /** A 1px outline while visible (a selected row with a border). */
  borderColor?: string;
}

// Web-only CSS: behind the content, never hit-tested, fading with the same
// 150ms the hover language uses for colour (HOVER_TRANSITION).
const PLATE_BASE = {
  position: 'absolute',
  zIndex: -1,
  pointerEvents: 'none',
  transition: 'background-color 0.15s ease, border-color 0.15s ease',
} as const;

/** An edge offset for `bleed` n: outward is negative. Never -0, which jest's toEqual tells apart from 0. */
const edge = (n: number) => (n === 0 ? 0 : -n);

const Plate: React.FC<HighlightPlateProps> = ({ visible, bleed = 0, color, radius, borderColor }) => {
  const { semantic } = useThemeColors();
  const radii = useRadius();
  const x = typeof bleed === 'number' ? bleed : bleed.x ?? 0;
  const y = typeof bleed === 'number' ? 0 : bleed.y ?? 0;
  // The tint is read only while painted: a partial test theme without the
  // hover token still mounts a resting plate.
  const fill = visible ? color ?? semantic?.background?.hover : 'transparent';
  return (
    <View
      style={[
        PLATE_BASE as any,
        {
          top: edge(y),
          bottom: edge(y),
          left: edge(x),
          right: edge(x),
          borderRadius: radius ?? radii.sm,
          backgroundColor: fill,
        },
        borderColor ? { borderWidth: 1, borderColor: visible ? borderColor : 'transparent' } : null,
      ]}
    />
  );
};

export const HighlightPlate: React.FC<HighlightPlateProps> = props =>
  Platform.OS === 'web' ? <Plate {...props} /> : null;
